import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { formatConversationTime } from '../conversations/relative-time.js';
import { COMPLETED_STEP_LINE, decisionNote } from '../copy/copy.js';
import { toolActivityLine } from '../copy/humanize.js';
import { MarkdownContent } from './markdown-content.js';
/**
 * Id PREFIX for optimistic just-sent user bubbles (never a wire event
 * id). Multiple in-flight sends get distinct suffixed ids
 * (`__sbui_chat_pending__-1`, `-2`, …) so sibling keys stay unique.
 */
export const PENDING_BUBBLE_ID = '__sbui_chat_pending__';
const NO_DURATIONS = new Map();
/**
 * Map the reduced `ChatMessage[]` onto grouped presentation items.
 * `createdAtById` resolves event-id → ISO timestamp (msg.id === event
 * id for wire-borne messages; the optimistic pending bubble has none).
 *
 * `turnDurationByEventId` (fn-49.3, R4): formatted completed-turn
 * durations keyed by the turn's attach event id (`computeTurnTimings`'
 * explicit attachment rule — the turn's last renderable event). The
 * containing item picks the duration up; ids that never render
 * (bootstrap rows) simply never match.
 */
export function buildChatItems(messages, createdAtById, turnDurationByEventId = NO_DURATIONS) {
    const items = [];
    const last = () => items[items.length - 1];
    /**
     * Attach a completed-turn duration to the CURRENT item when one of
     * the just-processed event ids is a turn's attach target. Bundled
     * results matter: an `agent.tool_result` / `user.custom_tool_result`
     * attach event surfaces as `result.id` on its tool_use row.
     */
    const attachDuration = (...eventIds) => {
        const tail = last();
        if (tail === undefined)
            return;
        for (const id of eventIds) {
            if (id === undefined)
                continue;
            const duration = turnDurationByEventId.get(id);
            if (duration !== undefined) {
                tail.duration = duration;
                return;
            }
        }
    };
    const pushBubble = (author, bubble) => {
        const tail = last();
        if (tail !== undefined && tail.kind === 'bubbles' && tail.author === author) {
            tail.bubbles.push(bubble);
            return;
        }
        items.push({
            kind: 'bubbles',
            // Keys are prefixed per item kind — sibling keys must stay
            // distinct (fn-47: duplicate sibling keys corrupt reconciliation).
            key: `bubbles-${bubble.id}`,
            author,
            bubbles: [bubble],
            timestamp: createdAtById.get(bubble.id) ?? null,
            duration: null,
        });
    };
    const pushStep = (step) => {
        const tail = last();
        if (tail !== undefined && tail.kind === 'activity') {
            tail.steps.push(step);
            return;
        }
        items.push({ kind: 'activity', key: `activity-${step.id}`, steps: [step], duration: null });
    };
    /** Fold into the matching step of the CURRENT activity run, if any. */
    const foldIntoStep = (toolUseId, patch) => {
        const tail = last();
        if (tail === undefined || tail.kind !== 'activity')
            return false;
        const idx = tail.steps.findIndex((s) => s.toolUseId === toolUseId);
        if (idx === -1)
            return false;
        const step = tail.steps[idx];
        if (step === undefined)
            return false;
        tail.steps[idx] = patch(step);
        return true;
    };
    for (const msg of messages) {
        switch (msg.kind) {
            case 'user': {
                if (msg.isBootstrap === true)
                    break;
                pushBubble('user', {
                    id: msg.id,
                    content: msg.content,
                    pending: msg.id.startsWith(PENDING_BUBBLE_ID),
                });
                attachDuration(msg.id);
                break;
            }
            case 'assistant': {
                if (msg.isBootstrapAck === true)
                    break;
                pushBubble('assistant', { id: msg.id, content: msg.content, pending: false });
                attachDuration(msg.id);
                break;
            }
            case 'tool_use': {
                pushStep({
                    id: msg.id,
                    toolUseId: msg.toolUseId,
                    rawToolName: msg.toolName,
                    mcpServerName: msg.mcpServerName ?? null,
                    friendly: toolActivityLine(msg.toolName),
                    input: msg.input,
                    output: msg.result?.output ?? null,
                    isError: msg.result?.isError === true,
                    decision: msg.decision ?? null,
                    denyMessage: msg.denyMessage ?? null,
                });
                // The attach target may be the tool_use event itself OR its
                // bundled result event (surfaced here as `result.id`).
                attachDuration(msg.id, msg.result?.id);
                break;
            }
            case 'tool_result': {
                // Usually bundled onto its tool_use by the reducer; a
                // standalone row means the originating tool_use was never
                // seen — fold by correlator when possible, else a quiet
                // detail-only step.
                const folded = foldIntoStep(msg.toolUseId, (step) => ({
                    ...step,
                    output: msg.output,
                    isError: msg.isError === true,
                }));
                if (!folded) {
                    pushStep({
                        id: msg.id,
                        toolUseId: msg.toolUseId,
                        rawToolName: null,
                        mcpServerName: null,
                        friendly: COMPLETED_STEP_LINE,
                        input: null,
                        output: msg.output,
                        isError: msg.isError === true,
                        decision: null,
                        denyMessage: null,
                    });
                }
                attachDuration(msg.id);
                break;
            }
            case 'tool_confirmation': {
                // Decisions reflect QUIETLY: onto the matching step when the
                // run is current, else as a decision-only line.
                const folded = foldIntoStep(msg.toolUseId, (step) => ({
                    ...step,
                    decision: msg.decision,
                    denyMessage: msg.denyMessage ?? null,
                }));
                if (!folded) {
                    pushStep({
                        id: msg.id,
                        toolUseId: msg.toolUseId,
                        rawToolName: null,
                        mcpServerName: null,
                        friendly: decisionNote(msg.decision),
                        input: null,
                        output: null,
                        isError: false,
                        decision: msg.decision,
                        denyMessage: msg.denyMessage ?? null,
                    });
                }
                break;
            }
            default:
                break;
        }
    }
    return items;
}
/**
 * One same-author run of bubbles with its single sparse timestamp.
 *
 * The fn-47/48 no-raw-HTML security rule holds on both branches:
 * user bubbles stay literal plain text (user input is not
 * markdown-authored), and assistant bubbles render markdown via
 * react-markdown, which satisfies the rule BY CONSTRUCTION — it
 * builds real React elements, never `dangerouslySetInnerHTML`, and
 * escapes/ignores raw HTML in the input (fn-49.2, R2/R3; safety
 * posture in markdown-content.tsx + the package README).
 *
 * The assistant wrapper is a `<div>`, not `<p>` — markdown emits
 * block elements (lists/tables/headings) and blocks inside a `<p>`
 * are invalid DOM.
 */
export function BubbleGroup(props) {
    const group = props.group;
    const time = group.timestamp !== null ? formatConversationTime(group.timestamp) : '';
    const showTime = group.timestamp !== null && time.length > 0;
    return (_jsxs("div", { className: `sbui-chat-group sbui-chat-group--${group.author}`, children: [group.bubbles.map((bubble) => group.author === 'assistant' ? (_jsx("div", { className: `sbui-chat-bubble sbui-chat-bubble--assistant${bubble.pending ? ' sbui-chat-bubble--pending' : ''}`, children: _jsx(MarkdownContent, { content: bubble.content }) }, `bubble-${bubble.id}`)) : (_jsx("p", { className: `sbui-chat-bubble sbui-chat-bubble--user${bubble.pending ? ' sbui-chat-bubble--pending' : ''}`, children: bubble.content }, `bubble-${bubble.id}`))), (showTime || group.duration !== null) && (_jsxs("div", { className: "sbui-chat-meta", children: [showTime && group.timestamp !== null && (_jsx("time", { className: "sbui-chat-time", dateTime: group.timestamp, children: time })), group.duration !== null && (_jsx("span", { className: "sbui-chat-turn-duration", children: group.duration }))] }))] }));
}
//# sourceMappingURL=bubbles.js.map